import { z } from 'zod'

import type { FinancialCommandClosure } from './financial-command'
import { InvalidApiResponseError } from '../api-errors'
import { isCalendarDate } from '../utils/calendar-date'
import {
  ContractorInvoiceBillingCadenceEnum,
  ContractorInvoiceStatusEnum,
} from './contractor-invoice'
import {
  ExpenseReimbursementStatusEnum,
  ExpenseWorkflowStatusEnum,
} from './expense'
import {
  FinancialCommandClosureSchema,
  FinancialCommandResultSchema,
} from './financial-command'

// Validate the wire without changing date representations or inventing missing defaults.
const instant = z.union([z.date(), z.string().datetime({ offset: true })])
const calendar = z.union([instant, z.string().refine(isCalendarDate)])
const money = z.number().finite()
const identity = {
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  memberId: z.string().uuid(),
  currency: z.string().trim().min(1).max(16),
  createdAt: instant,
  updatedAt: instant,
  financialCommand: FinancialCommandResultSchema.optional(),
}

/** Confirmation facts for a reimbursement, separate from a write-input schema.
 * Extra reader-projected fields remain unchanged; this is not server authorization.
 */
export const ExpenseFinancialResponseSchema = z
  .object({
    ...identity,
    expenseDate: calendar,
    workflowStatus: z.nativeEnum(ExpenseWorkflowStatusEnum),
    workflowStageId: z.string().min(1).max(128),
    reimbursementStatus: z.nativeEnum(ExpenseReimbursementStatusEnum),
    subtotal: money,
    taxAmount: money,
    tipAmount: money,
    totalAmount: money,
    reimbursedAmount: money,
    billable: z.boolean(),
    hasReimbursementHistory: z.boolean(),
    reimbursedAt: instant.nullable(),
  })
  .passthrough()

export const ContractorInvoiceFinancialResponseSchema = z
  .object({
    ...identity,
    invoiceNumber: z.string().min(1),
    status: z.nativeEnum(ContractorInvoiceStatusEnum),
    billingCadence: z.nativeEnum(ContractorInvoiceBillingCadenceEnum),
    invoiceStartDate: calendar,
    invoiceEndDate: calendar,
    totalHours: z.number().finite(),
    subtotal: money,
    totalAmount: money,
    paidAt: instant.nullable(),
  })
  .passthrough()

export interface FinancialResponseExpectation {
  recordId: string
  organizationId?: string
  operationId?: string
  /** Received transport metadata only; direct validation never guesses a status. */
  status?: number
}

/** Preserve the complete current reader projection, but require an actual matching record.
 * An issued command identity also requires its exact receipt before local intent can clear.
 * Never expose schema issues, record values or provider metadata in a protocol error.
 */
function confirm<T>(
  value: T,
  schema:
    | typeof ExpenseFinancialResponseSchema
    | typeof ContractorInvoiceFinancialResponseSchema,
  expected: FinancialResponseExpectation,
): T {
  const parsed = schema.safeParse(value)
  if (
    !parsed.success ||
    parsed.data.id.toLowerCase() !== expected.recordId.toLowerCase() ||
    (expected.organizationId !== undefined &&
      parsed.data.organizationId.toLowerCase() !==
        expected.organizationId.toLowerCase()) ||
    (expected.operationId !== undefined &&
      parsed.data.financialCommand?.operationId.toLowerCase() !==
        expected.operationId.toLowerCase())
  ) {
    throw new InvalidApiResponseError(expected.status)
  }
  return value
}

export const confirmExpenseFinancialResponse = <T>(
  value: T,
  expected: FinancialResponseExpectation,
): T => confirm(value, ExpenseFinancialResponseSchema, expected)
export const confirmContractorInvoiceFinancialResponse = <T>(
  value: T,
  expected: FinancialResponseExpectation,
): T => confirm(value, ContractorInvoiceFinancialResponseSchema, expected)

export function confirmFinancialCommandClosure<T>(
  value: T,
  expected: Omit<FinancialCommandClosure, 'outcome'> & { status?: number },
): T {
  const parsed = FinancialCommandClosureSchema.safeParse(value)
  if (
    !parsed.success ||
    parsed.data.action !== expected.action ||
    !(['operationId', 'organizationId', 'userId', 'targetId'] as const).every(
      key => parsed.data[key].toLowerCase() === expected[key].toLowerCase(),
    )
  )
    throw new InvalidApiResponseError(expected.status)
  return value
}
